import React, { useCallback, useEffect, useRef, useState } from 'react';
import cashReservesService from '../../services/cashReservesService';
import {
  invalidateFeeSnapshots,
  readFeeSnapshots,
  writeFeeSnapshots,
} from './feeSnapshotCache';
import { fmtCurrencyPrecise } from './helpers';

/**
 * One account's quarterly management-fee snapshots.
 *
 * Purely presentational — the disclosure below owns the fetch. Read-only by
 * design: a snapshot is minted by the daily ingest from what the reserve
 * actually was at quarter end, so there is nothing here for a user to edit.
 * Attributing a fee payment to a quarter happens where the payment is — in the
 * transactions panel's note editor, which offers these in its reserve picker.
 */
const FeeSnapshotsList = ({ accountNumber, accountName, showAccount, snapshots = [] }) => {
  if (snapshots.length === 0) return null;

  return (
    <div className="mb-2">
      {showAccount && (
        <div className="fw-semibold mb-1" title={`${accountName} · ${accountNumber}`}>
          {accountName || accountNumber}
        </div>
      )}
      <ul className="list-unstyled mb-0 small">
        {snapshots.map((row) => {
          const links = row.linked_transactions || [];
          return (
            <li
              key={row.reserve_id}
              className="d-flex flex-wrap align-items-center gap-2 mb-1"
            >
              <span className="fw-semibold">{row.reserve_category}</span>
              {/* Two different things, deliberately shown side by side: the
                  reserve is a cash buffer, the billed figure is the invoice.
                  Reserved is always dollars — the backend normalizes a
                  percent-of-account fee against the quarter-end account value
                  before freezing it, so there is no percent case to render. */}
              <span className="text-muted">
                reserved {fmtCurrencyPrecise(row.reserve_amount)}
              </span>
              <span className="text-muted">
                billed{' '}
                {row.billed == null ? (
                  <span title="No management-fee transaction recorded for this quarter — it may not have posted yet, or the quarter predates our stored transaction history.">
                    —
                  </span>
                ) : (
                  <span
                    title={
                      row.billed_transaction_count === 1
                        ? '1 fee transaction'
                        : `${row.billed_transaction_count} fee transactions`
                    }
                  >
                    {fmtCurrencyPrecise(row.billed)}
                  </span>
                )}
              </span>
              <span className="badge bg-light text-secondary border" title={row.note || undefined}>
                as of {row.quarter_end || '—'}
              </span>
              {links.length > 0 ? (
                <span className="badge bg-success-subtle text-success-emphasis border">
                  <i className="bi bi-link-45deg me-1"></i>
                  Linked{links[0].trade_date ? ` · ${links[0].trade_date}` : ''}
                  {links.length > 1 ? ` (+${links.length - 1})` : ''}
                </span>
              ) : (
                <span className="badge bg-light text-secondary border">Not linked</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * Collapsed "Quarterly Fee Snapshots" section that fetches only when opened.
 *
 * Tamarac carries one Management Fee reserve whose value moves with the
 * portfolio, so nothing in the data says which quarter a given fee withdrawal
 * paid for. The ingest freezes a dated copy at each quarter end; this lists
 * them and shows which ones a withdrawal has been attributed to.
 *
 * Mirrors ReserveLineageDisclosure deliberately, down to the request-id guard:
 * `accounts` is the FETCH scope and `visibleAccounts` the RENDER scope, so
 * ticking an account checkbox re-renders without refiring a single request.
 * Reads the batched /cash-reserves/fee-snapshots endpoint — one request for the
 * whole family, rather than the per-account lineage endpoint whose retired-
 * reserve and suggestion work this section never renders.
 */
const FeeSnapshotsDisclosure = ({ accounts, visibleAccounts, reload }) => {
  const [open, setOpen] = useState(false);
  const [byAccount, setByAccount] = useState(null); // null = not loaded yet
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  // Highest reload token already acted on, so re-renders and open/close toggles
  // can't refire a refresh that has been served.
  const handledTokenRef = useRef(0);
  // The same value, readable from the bulk load's async callback WITHOUT making
  // that effect depend on it — depending on it would refetch the whole family
  // on every single link save, which is the opposite of the point.
  const latestTokenRef = useRef(0);

  // The accounts array is rebuilt on every filter/sort/reload, so depending on
  // its identity would refetch forever.
  const accountKey = accounts
    .map((a) => String(a.account_number))
    .sort()
    .join(',');
  const shown = visibleAccounts || accounts;
  const showAccount = shown.length > 1;
  const reloadToken = reload?.token ?? 0;
  const reloadAccount = reload?.accountNumber;
  latestTokenRef.current = reloadToken;

  // One batched request for however many accounts are asked for, writing each
  // through the cache. `isCurrent` mirrors TransactionsPanel.fetchAccount: a
  // reload that resolves after a newer bulk load must not overwrite it.
  const load = useCallback(async (numbers, isCurrent = () => true) => {
    if (numbers.length === 0) return;
    const { accounts: fetched } = await cashReservesService.getFeeSnapshots(numbers);
    // Guard BEFORE the cache write, not just before the state update: the cache
    // is module scope, so a superseded response written here would outlive this
    // component and serve stale Linked badges on the next open.
    if (!isCurrent()) return;
    numbers.forEach((n) => writeFeeSnapshots(n, (fetched || {})[n] || []));
    setByAccount((prev) => ({
      ...(prev || {}),
      ...Object.fromEntries(numbers.map((n) => [n, (fetched || {})[n] || []])),
    }));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const requestId = (requestIdRef.current += 1);
    const isCurrent = () => requestId === requestIdRef.current;
    // Claim any pending token NOW, not when this resolves: effects run in
    // declaration order, so the single-account effect below fires immediately
    // after this one starts. Marking it later would let a link saved while the
    // section was closed trigger a redundant refetch of an account this bulk
    // load is already fetching.
    handledTokenRef.current = latestTokenRef.current;
    setError('');
    const numbers = accountKey ? accountKey.split(',') : [];

    // Serve whatever is already cached immediately and ask only for the rest —
    // reopening a section, or opening a family that overlaps one already seen,
    // then costs no request at all.
    const cached = {};
    const missing = [];
    numbers.forEach((n) => {
      const hit = readFeeSnapshots(n);
      if (hit !== null) cached[n] = hit;
      else missing.push(n);
    });
    setByAccount((prev) => ({ ...(prev || {}), ...cached }));
    if (missing.length === 0) {
      setIsLoading(false);   // fully cached: no spinner flash
      return () => {
        requestIdRef.current += 1;
      };
    }

    setIsLoading(true);
    load(missing, isCurrent)
      .catch(() => {
        if (isCurrent()) {
          setError(`Could not load fee snapshots for ${missing.length} account(s).`);
        }
      })
      .finally(() => {
        if (isCurrent()) setIsLoading(false);
      });
    return () => {
      requestIdRef.current += 1;
    };
  }, [open, accountKey, load]);

  // A transaction link was saved, cleared, or applied on one account: re-read
  // just that account so its Linked badge updates in place. Nothing to do while
  // the section is closed — it loads everything fresh when opened. A failure
  // here costs only a stale badge, so it stays silent rather than replacing the
  // whole section with an error.
  useEffect(() => {
    if (!open || !reloadToken || !reloadAccount) return;
    // Avoid racing the initial bulk load; let it settle, then refresh the one account.
    if (isLoading) return;
    if (reloadToken === handledTokenRef.current) return;
    handledTokenRef.current = reloadToken;
    const requestId = requestIdRef.current;
    // The cached copy is what just went stale, so drop it before refetching —
    // otherwise a later reopen would serve the pre-link badge.
    invalidateFeeSnapshots(reloadAccount);
    load([reloadAccount], () => requestId === requestIdRef.current).catch(() => {});
  }, [open, reloadToken, reloadAccount, isLoading, load]);

  const lists = shown
    .map((a) => ({ account: a, snapshots: byAccount?.[a.account_number] || [] }))
    .filter((entry) => entry.snapshots.length > 0);

  return (
    <div className="mb-3">
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
        <span className="text-uppercase small" style={{ letterSpacing: '0.05em' }}>
          Quarterly Fee Snapshots
        </span>
      </button>
      {open && (
        <div className="mt-2">
          <div className="text-muted small mb-2">
            Tamarac keeps one Management Fee reserve whose value moves with the portfolio. These
            are frozen copies taken at each quarter end, so a fee withdrawal can be linked to the
            quarter it paid for — pick one in a transaction&apos;s note editor. Reserved and billed
            differ by design: the reserve is a cash buffer sized as a share of the account, while
            billed is what the quarter&apos;s <em>Expense (Management Fee)</em> transactions
            actually came to.
          </div>
          {error && (
            <div className="alert alert-danger py-1 px-2 small" role="alert">
              {error}
            </div>
          )}
          {isLoading ? (
            <div className="text-muted small">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
              Loading fee snapshots…
            </div>
          ) : lists.length === 0 ? (
            <div className="text-muted small">No quarterly fee snapshots yet.</div>
          ) : (
            lists.map(({ account, snapshots }) => (
              <FeeSnapshotsList
                key={account.account_number}
                accountNumber={account.account_number}
                accountName={account.account_name}
                showAccount={showAccount}
                snapshots={snapshots}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FeeSnapshotsDisclosure;
